import { useCallback, useEffect, useMemo } from 'react';
import { FormState } from '@MyRequests/helpers.ts';
import { useDryRunCreateUserAccessRequest, useGetUserSession } from '@apono/client-app-sdk';
import { createAccessRequestClientModel, isFormPartiallyFull } from '@NewRequest/utils/helpers.ts';
import { useFlagFixed } from '@common/utils/flags/use-flag-fixed.ts';
import { Flags } from '@common/utils/flags/feature-flags.ts';
import { useDebounceCallback } from 'usehooks-ts';

export const JUSTIFICATION = 'justification';
const FIELD_MISSING = 'FIELD_MISSING';
const INVALID_DURATION = 'INVALID_DURATION';
const DURATION_IN_SEC = 'duration_in_sec';

export function useDryRunAccessRequest({
  formState,
  withDuration = true,
}: {
  formState: FormState;
  withDuration?: boolean;
}) {
  const { isEnabled: isSetDurationEnabled } = useFlagFixed(Flags.SHOW_SET_ACCESS_DURATION);
  const { data: userSession, isFetched: isUserSessionFetched } = useGetUserSession();

  const userSettingRequireDuration = userSession?.settings.require_duration_for_access_request ?? false;
  const userSettingRequestCustomFields = userSession?.settings.request_custom_fields;

  const {
    dryRunCreateUserAccessRequest,
    isLoading: isDryRunLoading,
    data: dryRunResponse,
    isSuccess: isDryRunSuccess,
  } = useDryRunCreateUserAccessRequest();

  const dryRunCallback = useCallback(
    (formData: FormState) => {
      const model = createAccessRequestClientModel(formData);
      dryRunCreateUserAccessRequest({ createAccessRequestClientModel: model });
    },
    [dryRunCreateUserAccessRequest],
  );

  const handleDryRun = useDebounceCallback(dryRunCallback, 300);

  useEffect(() => {
    if (isFormPartiallyFull(formState)) handleDryRun(formState);
  }, [formState, handleDryRun]);

  const isJustificationRequired = useMemo(() => {
    if (!isDryRunSuccess) return true;
    return (
      dryRunResponse?.errors.some((error) => error.code === FIELD_MISSING && error.field === JUSTIFICATION) ?? true
    );
  }, [isDryRunSuccess, dryRunResponse]);

  const isCustomFieldsFilled = useMemo(() => {
    if (!userSettingRequestCustomFields) {
      return true;
    }
    return userSettingRequestCustomFields.every((customField) => {
      if (!customField.required) {
        return true;
      }
      const fieldValue = formState.custom_fields?.[customField.id];
      return !!fieldValue;
    });
  }, [formState, userSettingRequestCustomFields]);

  const durationErrorMessage = useMemo(() => {
    if (!isDryRunSuccess || !userSettingRequireDuration) return null;
    const error = dryRunResponse?.errors.find(
      (error) => error.code === INVALID_DURATION && error.field === DURATION_IN_SEC,
    );
    return error?.message ?? null;
  }, [isDryRunSuccess, dryRunResponse, userSettingRequireDuration]);

  const showDuration = withDuration && isUserSessionFetched && userSettingRequireDuration && isSetDurationEnabled;

  const isDryRunErrors =
    (showDuration && (durationErrorMessage || formState.selectedDuration === null)) ||
    (isJustificationRequired && !formState.justification) ||
    !isCustomFieldsFilled ||
    isDryRunLoading;

  return {
    handleDryRun,
    showDuration,
    isDryRunErrors,
    isDryRunLoading,
    durationErrorMessage,
  };
}
